<template>
  <!-- Sticky progress bar — hidden on birth year step since it's a pre-questionnaire screen -->
  <div class="t2-progress-bar" v-if="!onBirthYearStep">
    <v-progress-linear
      :model-value="totalProgress"
      color="primary"
      height="4"
    />
    <div class="text-caption text-center text-medium-emphasis py-1">
      Secțiunea {{ currentSectionIndex + 1 }} din {{ sections.length }}
    </div>
  </div>

  <v-container class="py-6" style="max-width: 680px;">

    <!-- Birth year step: shown before the questionnaire sections -->
    <template v-if="onBirthYearStep">
      <v-chip color="primary" variant="tonal" size="small" class="mb-3">Despre tine</v-chip>
      <p class="text-body-2 text-medium-emphasis mb-6">
        Anul nașterii ne ajută să evaluăm compatibilitatea etapelor de viață.
      </p>

      <v-card rounded="lg" elevation="1" class="pa-6 mb-4" :class="{ 'card-unanswered': submitted && !birthYearValid }">
        <p class="text-body-1 font-weight-medium mb-4">În ce an te-ai născut?</p>
        <v-text-field
          v-model="birthYear"
          type="number"
          :min="1900"
          :max="maxBirthYear"
          placeholder="ex. 1990"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          :error-messages="submitted && !birthYearValid ? ['Introdu un an valid (trebuie să ai cel puțin 18 ani)'] : []"
          color="primary"
        />
      </v-card>

      <div class="d-flex justify-end mt-6 mb-10">
        <v-btn color="primary" append-icon="mdi-arrow-right" @click="confirmBirthYear">
          Continuă
        </v-btn>
      </div>
    </template>

    <!-- Questionnaire sections -->
    <template v-else>

      <!-- Section header -->
      <div class="d-flex align-center gap-2 mb-3">
        <v-chip color="primary" variant="tonal" size="small">{{ currentSection.label }}</v-chip>
        <v-btn v-if="isDevelopment" variant="tonal" color="warning" size="x-small" @click="prefill">Dev: Fill</v-btn>
      </div>
      <p class="text-body-2 text-medium-emphasis mb-6">
        {{ currentSection.instruction }}
      </p>

      <!-- One card per question -->
      <v-card
        v-for="question in currentSection.items"
        :key="question.id"
        rounded="lg"
        elevation="1"
        class="pa-6 mb-4"
        :class="{ 'card-unanswered': submitted && !isAnswered(question) }"
        :data-question-id="question.id"
      >

        <!-- A/B slider: distributes 5 points between two poles -->
        <template v-if="question.type === 'ab-slider'">
          <p class="text-body-1 font-weight-medium mb-5" style="line-height: 1.6;">
            {{ question.context }}
          </p>
          <div class="ab-labels text-body-2 mb-1">
            <span class="text-medium-emphasis">A. {{ question.labelA }}</span>
            <span class="text-medium-emphasis">B. {{ question.labelB }}</span>
          </div>
          <v-slider
            :model-value="answers[question.id] ?? 2"
            @update:model-value="onSliderChange(question.id, $event)"
            min="0"
            max="5"
            step="1"
            reverse
            show-ticks="always"
            tick-size="4"
            color="primary"
            hide-details
            class="mt-2"
          />
          <div class="text-caption text-center text-medium-emphasis mt-1">
            {{ sliderLabel(answers[question.id]) }}
          </div>
        </template>

        <!-- Standard Likert radio group -->
        <template v-else>
          <p class="text-body-1 font-weight-medium mb-4" style="line-height: 1.6;">
            {{ question.text }}
          </p>
          <v-radio-group v-model="answers[question.id]" hide-details color="primary">
            <v-radio
              v-for="(label, idx) in (question.scaleLabels || currentSection.scaleLabels)"
              :key="idx"
              :label="`${(question.scaleMin || currentSection.scaleMin) + idx} — ${label}`"
              :value="(question.scaleMin || currentSection.scaleMin) + idx"
              color="primary"
              class="mb-1"
            />
          </v-radio-group>
        </template>

      </v-card>

      <!-- Navigation -->
      <div class="d-flex justify-space-between mt-6 mb-10">
        <v-btn
          variant="text"
          prepend-icon="mdi-arrow-left"
          @click="goPrev"
        >
          Înapoi
        </v-btn>

        <v-btn
          v-if="!isLastSection"
          color="primary"
          append-icon="mdi-arrow-right"
          @click="goNext"
        >
          Continuă
        </v-btn>

        <v-btn
          v-else
          color="primary"
          :loading="saving"
          append-icon="mdi-check"
          @click="saveAnswers"
        >
          Salvează
        </v-btn>
      </div>

    </template>

  </v-container>
</template>

<script>
import { appStore } from '@/stores/AppStore'
import apiService from '@/services/ApiService'
import questionsData from '@/data/t2-questions.json'
import envMixin from '@/mixins/envMixin'

// Labels for each A-side point value (0 = fully B, 5 = fully A)
const SLIDER_LABELS = ['Complet B', 'Mult mai mult B', 'Puțin mai mult B', 'Puțin mai mult A', 'Mult mai mult A', 'Complet A']

export default {
  name: 'ProfileT2View',
  mixins: [envMixin],

  data() {
    return {
      onBirthYearStep: true,  // shown first, before questionnaire sections
      birthYear: null,         // collected here, sent to backend with T2 answers for S_age scoring
      currentSectionIndex: 0,
      answers: {},
      // Tracks which ab-slider items the user has explicitly interacted with.
      // Needed because slider value 0 is valid, so answers[id] == null alone
      // cannot distinguish "not yet answered" from "answered with 0".
      sliderTouched: {},
      saving: false,
      submitted: false,
    }
  },

  computed: {
    store() {
      return appStore()
    },

    // Maximum valid birth year: user must be at least 18 years old
    maxBirthYear() {
      return new Date().getFullYear() - 18
    },

    birthYearValid() {
      const y = parseInt(this.birthYear, 10)
      return y >= 1900 && y <= this.maxBirthYear
    },

    sections() {
      return questionsData.sections
    },

    currentSection() {
      return this.sections[this.currentSectionIndex]
    },

    isLastSection() {
      return this.currentSectionIndex === this.sections.length - 1
    },

    isSectionComplete() {
      return this.currentSection.items.every(q => this.isAnswered(q))
    },

    totalProgress() {
      const total = this.sections.reduce((sum, s) => sum + s.items.length, 0)
      const answered = Object.keys(this.answers).length
      return (answered / total) * 100
    },
  },

  methods: {
    // Returns true when the question has a valid recorded answer.
    isAnswered(question) {
      if (question.type === 'ab-slider') return this.sliderTouched[question.id] === true
      return this.answers[question.id] != null
    },

    // Called when the user moves an ab-slider. Records both the value and
    // the "touched" state so that value 0 can be distinguished from "not answered".
    onSliderChange(id, value) {
      this.answers[id]      = value
      this.sliderTouched[id] = true
    },

    // Human-readable label for the current slider position.
    sliderLabel(value) {
      if (value == null) return 'Mută cursorul pentru a răspunde'
      return SLIDER_LABELS[value] ?? ''
    },

    // Validates birth year and advances to the questionnaire sections
    confirmBirthYear() {
      this.submitted = true
      if (!this.birthYearValid) return
      this.submitted = false
      this.onBirthYearStep = false
      window.scrollTo(0, 0)
    },

    goNext() {
      this.submitted = true
      if (!this.isSectionComplete) {
        this.scrollToFirstUnanswered()
        return
      }
      this.submitted = false
      window.scrollTo(0, 0)
      this.currentSectionIndex++
    },

    goPrev() {
      this.submitted = false
      window.scrollTo(0, 0)
      // Go back to birth year step from the first questionnaire section
      if (this.currentSectionIndex === 0) {
        this.onBirthYearStep = true
      } else {
        this.currentSectionIndex--
      }
    },

    scrollToFirstUnanswered() {
      const firstUnanswered = this.currentSection.items.find(q => !this.isAnswered(q))
      if (!firstUnanswered) return
      this.$nextTick(() => {
        const el = document.querySelector(`[data-question-id="${firstUnanswered.id}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },

    prefill() {
      const filled  = {}
      const touched = {}
      questionsData.sections.forEach(section => {
        section.items.forEach((item, idx) => {
          if (item.type === 'ab-slider') {
            filled[item.id]  = idx % 6
            touched[item.id] = true
          } else {
            const min = item.scaleMin ?? section.scaleMin
            const max = item.scaleMax ?? section.scaleMax
            filled[item.id] = min + (idx % (max - min + 1))
          }
        })
      })
      this.answers      = filled
      this.sliderTouched = touched
    },

    async saveAnswers() {
      this.submitted = true
      if (!this.isSectionComplete) {
        this.scrollToFirstUnanswered()
        return
      }
      this.saving = true
      try {
        const data = await apiService.submitT2Answers(this.answers, parseInt(this.birthYear, 10))
        this.store.setUser({ ...this.store.user, profileState: data.profileState })
        // scrollTo param tells MeView to scroll to the T2 section so the user
        // can immediately see that generation is in progress.
        this.$router.push({ path: '/me', query: { scrollTo: 'section-attachment' } })
      } catch (err) {
        console.error('T2 save error:', err)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.card-unanswered {
  border: 2px solid rgb(var(--v-theme-error)) !important;
}

.t2-progress-bar {
  position: sticky;
  top: 0;
  z-index: 99;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* A/B labels: pole A on the left, pole B on the right, above the slider */
.ab-labels {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  line-height: 1.5;
}
</style>
